import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { getReadings, getEmployees, saveMeterReview, analyzeMeterReading } from "../services/api";
import { ChevronLeft, ChevronRight, Check, SkipForward, Filter } from "lucide-react";

export default function RevisionRapidaPage() {
  const [readings, setReadings] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterUser, setFilterUser] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [index, setIndex] = useState(0);
  const [form, setForm] = useState({ meter_code: "", reading_value: "", notes: "" });
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [zoomed, setZoomed] = useState(false);
  const [origin, setOrigin] = useState("50% 50%");
  const codeRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    const PAGE = 500;

    async function fetchAll() {
      const [first, emps] = await Promise.all([
        getReadings({ limit: PAGE, page: 1 }),
        getEmployees(),
      ]);
      const total = first.pagination?.total ?? first.data?.length ?? 0;
      const pages = Math.ceil(total / PAGE);
      let all = first.data || [];
      if (pages > 1) {
        const rest = await Promise.all(
          Array.from({ length: pages - 1 }, (_, i) =>
            getReadings({ limit: PAGE, page: i + 2 }).then((r) => r.data || []),
          ),
        );
        all = all.concat(rest.flat());
      }
      setReadings(all);
      setEmployees(emps || []);
    }

    fetchAll()
      .catch(() => showToast("Error al cargar lecturas", "error"))
      .finally(() => setLoading(false));
  }, []);

  const meters = useMemo(() => {
    return readings.filter((r) => {
      // Nunca mostrar ilegibles ni los que ya tienen código Y medición
      if (r.meter_code === "ilegible") return false;
      if (r.meter_code && r.reading_value) return false;
      if (filterUser && r.user_id !== filterUser) return false;
      if (filterStatus === "no_code") return !r.meter_code;
      if (filterStatus === "no_value") return r.meter_code && !r.reading_value;
      return true;
    });
  }, [readings, filterUser, filterStatus]);

  const current = meters[index] ?? null;

  // Sync form when slide changes
  useEffect(() => {
    if (current) {
      setForm({
        meter_code: current.meter_code || "",
        reading_value: current.reading_value || "",
        notes: current.notes || "",
      });
    }
  }, [index, current?.id]);

  // Focus code input when slide changes
  useEffect(() => {
    if (codeRef.current) codeRef.current.focus();
  }, [index]);

  // Reset index when filters change
  useEffect(() => {
    setIndex(0);
  }, [filterUser, filterStatus]);

  // Reset zoom when slide changes
  useEffect(() => {
    setZoomed(false);
    setOrigin("50% 50%");
  }, [index]);

  // Keyboard navigation (only when not typing in inputs)
  useEffect(() => {
    function onKey(e) {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      if (e.key === "ArrowRight" || e.key === "ArrowDown") goNext();
      if (e.key === "ArrowLeft" || e.key === "ArrowUp") goPrev();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  function showToast(msg, type = "success") {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }

  function goPrev() {
    setIndex((i) => Math.max(0, i - 1));
  }

  function goNext() {
    setIndex((i) => Math.min(meters.length - 1, i + 1));
  }

  const handleSaltar = useCallback(async () => {
    if (!current) return;
    setSaving(true);
    try {
      await saveMeterReview({
        meter_id: current.id,
        meter_code: "ilegible",
        reading_value: form.reading_value || null,
        notes: form.notes || null,
        status: "reviewed",
      });
      setReadings((prev) =>
        prev.map((r) =>
          r.id === current.id ? { ...r, meter_code: "ilegible" } : r,
        ),
      );
      setIndex((i) => Math.min(meters.length - 1, i + 1));
    } catch (err) {
      showToast(err.message || "Error al guardar", "error");
    } finally {
      setSaving(false);
    }
  }, [current, form.reading_value, meters.length]);

  const handleAnalizar = useCallback(async () => {
    if (!current?.image_url) return;
    setAnalyzing(true);
    try {
      const result = await analyzeMeterReading(current.image_url, current.id);
      setForm((f) => ({
        ...f,
        meter_code: result.numero_medidor || f.meter_code,
        reading_value: result.lectura_m3 && result.lectura_m3 !== "REVISAR" ? result.lectura_m3 : f.reading_value,
      }));
      if (result.lectura_m3 === "REVISAR") {
        showToast("No se pudo leer la medición con certeza — revisá manualmente", "error");
      } else {
        showToast("Lectura detectada");
      }
    } catch (err) {
      showToast(err.message || "Error al analizar", "error");
    } finally {
      setAnalyzing(false);
    }
  }, [current]);

  const handleRevisar = useCallback(async () => {
    if (!current) return;
    setSaving(true);
    try {
      await saveMeterReview({
        meter_id: current.id,
        meter_code: form.meter_code || null,
        reading_value: form.reading_value || null,
        notes: form.notes || null,
        status: "reviewed",
      });

      // Update local readings state
      setReadings((prev) =>
        prev.map((r) =>
          r.id === current.id
            ? { ...r, meter_code: form.meter_code, reading_value: form.reading_value, notes: form.notes }
            : r,
        ),
      );

      showToast("Revisión guardada");
      setIndex((i) => Math.min(meters.length - 1, i + 1));
    } catch (err) {
      showToast(err.message || "Error al guardar", "error");
    } finally {
      setSaving(false);
    }
  }, [current, form, meters.length]);

  const totalComplete = readings.filter((r) => r.meter_code && r.reading_value).length;
  const totalPending  = readings.filter((r) => !r.meter_code || !r.reading_value).length;

  if (loading) {
    return <div style={s.fullCenter}>Cargando lecturas...</div>;
  }

  return (
    <div style={s.root}>
      {/* Header */}
      <div style={s.topBar}>
        <div style={s.titleBlock}>
          <h1 style={s.title}>Revisión Rápida</h1>
          <span style={s.subtitle}>
            {totalComplete} completos · {totalPending} pendientes de {readings.length} lecturas
          </span>
        </div>
        <button style={s.filterToggle} onClick={() => setShowFilters((v) => !v)}>
          <Filter size={14} style={{ marginRight: 6 }} />
          Filtros
        </button>
      </div>

      {/* Filters */}
      {showFilters && (
        <div style={s.filterBar}>
          <select
            style={s.select}
            value={filterUser}
            onChange={(e) => setFilterUser(e.target.value)}
          >
            <option value="">Todos los usuarios</option>
            {employees.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
          <select
            style={s.select}
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">Todos los pendientes</option>
            <option value="no_code">Sin código</option>
            <option value="no_value">Con código, sin medición</option>
          </select>
          <span style={s.resultCount}>
            {meters.length} lectura{meters.length !== 1 ? "s" : ""}
          </span>
        </div>
      )}

      {meters.length === 0 ? (
        <div style={s.empty}>
          <p style={{ fontSize: 32, margin: "0 0 12px" }}>✅</p>
          <p style={{ color: "var(--text-2)", fontSize: 15 }}>
            {readings.length === 0
              ? "No hay lecturas cargadas"
              : "No hay lecturas que coincidan con el filtro"}
          </p>
        </div>
      ) : (
        <>
          {/* Progress bar */}
          <div style={s.progressWrap}>
            <div
              style={{
                ...s.progressBar,
                width: `${((index + 1) / meters.length) * 100}%`,
              }}
            />
          </div>
          <div style={s.progressLabel}>
            <span style={s.progressText}>
              {index + 1} / {meters.length}
            </span>
            {current?.user_name && (
              <span style={s.userChip}>👤 {current.user_name}</span>
            )}
            {current?.zone && (
              <span style={s.zoneChip}>{current.zone}</span>
            )}
          </div>

          {/* Card */}
          <div style={s.card}>
            {/* Photo */}
            <div style={{ ...s.photoWrap, overflow: "hidden" }}>
              {current?.image_url ? (
                <img
                  src={current.image_url}
                  alt="Medidor"
                  style={{
                    ...s.photo,
                    transform: zoomed ? "scale(2.5)" : "scale(1)",
                    transformOrigin: origin,
                    transition: zoomed ? "transform 0.15s ease" : "transform 0.2s ease",
                    cursor: zoomed ? "zoom-out" : "zoom-in",
                  }}
                  onClick={(e) => {
                    if (!zoomed) {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const x = ((e.clientX - rect.left) / rect.width) * 100;
                      const y = ((e.clientY - rect.top) / rect.height) * 100;
                      setOrigin(`${x}% ${y}%`);
                    }
                    setZoomed((z) => !z);
                  }}
                  onMouseMove={(e) => {
                    if (!zoomed) return;
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = ((e.clientX - rect.left) / rect.width) * 100;
                    const y = ((e.clientY - rect.top) / rect.height) * 100;
                    setOrigin(`${x}% ${y}%`);
                  }}
                />
              ) : (
                <div style={s.noPhoto}>Sin foto</div>
              )}
              {current?.meter_code && current?.reading_value && (
                <div style={s.completeBadge}>
                  <Check size={13} style={{ marginRight: 4 }} />
                  Completo
                </div>
              )}
            </div>

            {/* Form */}
            <div style={s.formSide}>
              <div style={s.fieldGroup}>
                <label style={s.label}>Código de medidor</label>
                <input
                  ref={codeRef}
                  style={s.input}
                  placeholder="Ingresá el código"
                  value={form.meter_code}
                  onChange={(e) => setForm((f) => ({ ...f, meter_code: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === "Enter") handleRevisar(); }}
                />
              </div>

              <div style={s.fieldGroup}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 7 }}>
                  <label style={{ ...s.label, margin: 0 }}>Valor de medición</label>
                  {current?.image_url && (
                    <button
                      style={{ ...s.btnIA, opacity: analyzing ? 0.7 : 1, cursor: analyzing ? "not-allowed" : "pointer" }}
                      onClick={handleAnalizar}
                      disabled={analyzing}
                    >
                      {analyzing ? "Analizando..." : "✨ Analizar IA"}
                    </button>
                  )}
                </div>
                <input
                  style={s.input}
                  placeholder="Ej: 12345"
                  value={form.reading_value}
                  onChange={(e) => setForm((f) => ({ ...f, reading_value: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === "Enter") handleRevisar(); }}
                />
              </div>

              <div style={s.fieldGroup}>
                <label style={s.label}>Observación</label>
                <textarea
                  style={s.textarea}
                  placeholder="Opcional..."
                  value={form.notes}
                  rows={2}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                />
              </div>

              <div style={s.btnRow}>
                <button
                  style={{ ...s.btnSkip, opacity: saving ? 0.7 : 1 }}
                  onClick={handleSaltar}
                  disabled={saving || index >= meters.length - 1}
                  title="Marcar como ilegible y pasar"
                >
                  <SkipForward size={15} style={{ marginRight: 6 }} />
                  Saltar
                </button>
                <button
                  style={{
                    ...s.btnRevisar,
                    opacity: saving ? 0.7 : 1,
                    cursor: saving ? "not-allowed" : "pointer",
                  }}
                  onClick={handleRevisar}
                  disabled={saving}
                >
                  <Check size={16} style={{ marginRight: 6 }} />
                  {saving ? "Guardando..." : "Guardar"}
                </button>
              </div>

              {current?.address && (
                <p style={s.address}>📍 {current.address}</p>
              )}
              {current?.latitude && !current?.address && (
                <a
                  href={`https://maps.google.com/?q=${current.latitude},${current.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={s.coordLink}
                >
                  📍 Ver en mapa
                </a>
              )}
              {current?.created_at && (
                <p style={s.date}>
                  {new Date(current.created_at).toLocaleDateString("es-AR", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              )}
            </div>
          </div>

          {/* Navigation */}
          <div style={s.navRow}>
            <button
              style={{ ...s.navBtn, opacity: index === 0 ? 0.35 : 1 }}
              onClick={goPrev}
              disabled={index === 0}
            >
              <ChevronLeft size={22} />
              Anterior
            </button>
            <button
              style={{ ...s.navBtn, opacity: index >= meters.length - 1 ? 0.35 : 1 }}
              onClick={goNext}
              disabled={index >= meters.length - 1}
            >
              Siguiente
              <ChevronRight size={22} />
            </button>
          </div>
        </>
      )}

{/* Toast */}
      {toast && (
        <div style={{ ...s.toast, background: toast.type === "error" ? "var(--error-text)" : "var(--success-text)" }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}

const s = {
  root: { width: "100%" },

  fullCenter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 300,
    color: "var(--text-2)",fontSize: 15,
  },

  topBar: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  titleBlock: { display: "flex", flexDirection: "column", gap: 4 },
  title: { fontSize: 24, fontWeight: 700, color: "var(--text)", margin: 0 },
  subtitle: { fontSize: 13, color: "var(--text-2)", fontWeight: 700 },

  filterToggle: {
    display: "flex",
    alignItems: "center",
    padding: "8px 14px",
    borderRadius: 10,

color: "var(--text-2)",
    cursor: "pointer",fontSize: 13,
    fontWeight: 700,
    fontFamily: "Montserrat, sans-serif",
  },

  filterBar: {
    display: "flex",
    alignItems: "center",
    gap: 10,

padding: "12px 16px",
    borderRadius: 12,
    marginBottom: 16,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    flexWrap: "wrap",
  },
  select: {
    padding: "7px 10px",
    borderRadius: 10,    

fontSize: 13,
    color: "var(--text)",
    outline: "none",
    fontFamily: "Montserrat, sans-serif",

},
  resultCount: {
    marginLeft: "auto",fontSize: 13,
    fontWeight: 700,
    color: "var(--text-2)",
    whiteSpace: "nowrap",
  },

  empty: { textAlign: "center", padding: "80px 0", color: "var(--text-2)" },

  progressWrap: {
    height: 5,
    background: "var(--border)",
    borderRadius: 99,
    overflow: "hidden",
    marginBottom: 8,
  },
  progressBar: {
    height: "100%",
    background: "#D4620D",
    borderRadius: 99,
    transition: "width 0.3s ease",
  },
  progressLabel: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  progressText: { fontSize: 13, fontWeight: 700, color: "var(--text-2)" },
  userChip: {
    fontSize: 13,
    fontWeight: 700,
    color: "var(--text-2)",
    background: "var(--bg-subtle)",
    borderRadius: 6,
    padding: "2px 9px",
  },
  zoneChip: {
    fontSize: 13,
    fontWeight: 700,
    color: "#D4620D",
    background: "var(--primary-bg)",
    borderRadius: 6,
    padding: "2px 9px",
  },

  card: {
    borderRadius: 14,
    boxShadow: "0 4px 24px rgba(0,0,0,0.09)",
    display: "flex",
    overflow: "hidden",
    minHeight: 380,
  },

  photoWrap: {
    flex: "0 0 52%",
    position: "relative",
    background: "#000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 380,
    maxHeight: "calc(100vh - 200px)",
    overflow: "hidden",
  },
  photo: {
    width: "100%",
    maxHeight: "calc(100vh - 200px)",
    objectFit: "contain",
    display: "block",
  },
  noPhoto: { color: "#d8d8d3", fontSize: 14, fontWeight: 700 },
  zoomBtn: {
    position: "absolute",
    top: 12,
    right: 12,
    background: "rgba(0,0,0,0.55)",
    border: "none",
    borderRadius: 10,
    color: "#fff",
    width: 36,
    height: 36,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  completeBadge: {
    position: "absolute",
    bottom: 12,
    left: 12,
    display: "flex",
    alignItems: "center",
    background: "var(--success-bg)",
    color: "var(--success-text)",
    borderRadius: 10,
    padding: "4px 10px",fontSize: 13,
    fontWeight: 700,
  },

  formSide: {
    flex: 1,
    padding: "28px 28px 24px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  fieldGroup: { marginBottom: 18 },
  label: {
    display: "block",fontSize: 13,
    fontWeight: 700,
    color: "var(--text-2)",
    marginBottom: 7,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  input: {
    width: "100%",
    padding: "11px 14px",
    borderRadius: 10,
    border: "1.5px solid var(--border)",fontSize: 15,
    outline: "none",
    fontFamily: "Montserrat, sans-serif",
    color: "var(--text)",
    fontWeight: 700,
    boxSizing: "border-box",
  },
  btnIA: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "3px 10px",
    borderRadius: 6,
    border: "none",
    background: "#7c3aed",
    color: "#fff",fontSize: 13,
    fontWeight: 700,
    fontFamily: "Montserrat, sans-serif",
  },
  textarea: {
    width: "100%",
    padding: "10px 14px",
    borderRadius: 10,
    border: "1.5px solid var(--border)",fontSize: 13,
    outline: "none",
    fontFamily: "Montserrat, sans-serif",
    color: "var(--text)",
    boxSizing: "border-box",
    resize: "none",
  },

  btnRow: {
    display: "flex",
    gap: 10,
    marginTop: 4,
    marginBottom: 14,
  },
  btnSkip: {
    display: "flex",
    alignItems: "center",
    padding: "11px 16px",
    borderRadius: 10,
    border: "1.5px solid var(--border)",

color: "var(--text-2)",
    cursor: "pointer",fontSize: 14,
    fontWeight: 700,
    fontFamily: "Montserrat, sans-serif",
  },
  btnRevisar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    padding: "11px 20px",
    borderRadius: 10,
    border: "none",
    background: "#D4620D",
    color: "#fff",
    fontWeight: 700,fontSize: 15,
    fontFamily: "Montserrat, sans-serif",
    boxShadow: "0 4px 14px rgba(212,98,13,0.35)",
  },

  address: { fontSize: 13, color: "var(--text-2)", margin: "0 0 4px" },
  coordLink: { fontSize: 13, color: "var(--text-2)", textDecoration: "none", display: "inline-block" },
  date: { fontSize: 13, color: "var(--text-2)", margin: "4px 0 0" },

  navRow: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: 18,
  },
  navBtn: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "10px 20px",
    borderRadius: 10,
    border: "1.5px solid var(--border)",

color: "var(--text-2)",
    cursor: "pointer",fontSize: 14,
    fontWeight: 700,
    fontFamily: "Montserrat, sans-serif",
  },

toast: {
    position: "fixed",
    bottom: 28,
    left: "50%",
    transform: "translateX(-50%)",
    color: "#fff",
    padding: "12px 24px",
    borderRadius: 10,
    fontWeight: 700,fontSize: 14,
    zIndex: 4000,
    boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
    pointerEvents: "none",
  },
};
